import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Home, MessageCircle, Clock, MapPin, Loader2, Banknote, CreditCard, Tag, Copy, Smartphone } from "lucide-react";
import { API, formatARS } from "@/lib/format";
import { fireConversiones } from "@/lib/tracking";

export default function Success() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsp, setWsp] = useState("");
  const [gads, setGads] = useState(undefined);
  const [dirLocal, setDirLocal] = useState("");

  useEffect(() => {
    axios.get(`${API}/config`)
      .then((r) => {
        setWsp((r.data.whatsappNumber || "").replace(/\D/g, ""));
        setGads(r.data.googleAds || {});
        setDirLocal(r.data.direccionLocal || "");
      })
      .catch(() => setGads({}));
  }, []);

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  // Conversión de compra: una sola vez por pedido (aunque recargue la página)
  useEffect(() => {
    if (order && gads !== undefined) fireConversiones(order, gads);
  }, [order, gads]);

  if (loading) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando pedido...
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-ink flex flex-col items-center justify-center text-white p-4">
        <div className="font-heading text-3xl uppercase mb-2">Pedido no encontrado</div>
        <Link to="/" className="text-brand underline uppercase text-sm tracking-widest">Volver al inicio</Link>
      </div>
    );
  }

  const isCash = (order.pagoDetalle?.tipo || (order.metodoPago === "efectivo" ? "efectivo" : null)) === "efectivo";
  const isTransfer = order.pagoDetalle?.tipo === "transferencia";

  const copyAlias = async () => {
    const alias = order?.pagoDetalle?.alias;
    if (!alias) return;
    try {
      await navigator.clipboard.writeText(alias);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = alias;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Alias copiado", { description: "Pegalo en Mercado Pago para transferir" });
  };

  const openMpApp = () => {
    window.location.href = "mercadopago://";
    setTimeout(() => {
      if (!document.hidden) window.open("https://www.mercadopago.com.ar", "_blank");
    }, 1200);
  };

  return (
    <div className="min-h-screen bg-ink grain relative">
      <div className="relative z-10 max-w-2xl mx-auto p-4 sm:p-8">
        <div data-testid="order-success-modal" className="slide-up bg-panel border border-line rounded-2xl overflow-hidden">
          <div className="p-6 sm:p-8 text-center border-b border-line bg-gradient-to-b from-brand/10 to-transparent">
            <div className="mx-auto w-16 h-16 rounded-full bg-brand flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-black" strokeWidth={3} />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 font-bold">
              {isTransfer ? "Esperando tu transferencia" : isCash ? "Pedido recibido" : "Pago confirmado"}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl uppercase text-white mt-2">
              Pedido <span className="text-brand">confirmado</span>
            </h1>
            <p className="text-sm text-zinc-400 mt-2">Te vamos a contactar cuando esté saliendo tu pedido</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-black/40 border border-line rounded-full px-4 py-1.5">
              <span className="text-xs uppercase tracking-widest text-zinc-500">Pedido</span>
              <span data-testid="order-id-display" className="font-heading text-lg text-gold">{order.codigo}</span>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {isTransfer ? (
              <div data-testid="transfer-info-box" className="p-4 bg-black/40 rounded-lg border border-[#009EE3]/40">
                {order.pagoDetalle?.alias && (
                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Alias para transferir</div>
                    <div data-testid="transfer-alias" className="font-heading text-2xl text-gold">{order.pagoDetalle.alias}</div>
                    <button
                      data-testid="copy-alias-button"
                      onClick={copyAlias}
                      className="mt-2 w-full flex items-center justify-center gap-2 bg-gold hover:bg-gold-strong text-black font-black uppercase text-xs tracking-widest py-3 rounded transition-colors"
                    >
                      <Copy className="w-4 h-4" /> Copiar alias
                    </button>
                  </div>
                )}
                {wsp && (
                  <a
                    data-testid="avisar-pago-wsp"
                    href={`https://wa.me/${wsp}?text=${encodeURIComponent(`Hola! Ya transferí el pedido ${order.codigo} (${formatARS(order.total)}). Te paso el comprobante.`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mb-3 w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB457] text-black font-black uppercase text-xs tracking-widest py-3 rounded transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={3} /> Ya transferí, enviar comprobante por WhatsApp
                  </a>
                )}
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-[#009EE3] mt-0.5" />
                  <div className="flex-1 text-sm">
                    <div className="font-bold text-white uppercase tracking-wide">{order.pagoDetalle?.nombre || "Transferencia"}</div>
                    {order.pagoDetalle?.cbu && (
                      <div className="mt-1.5 text-xs text-zinc-400"><span className="text-zinc-500 uppercase text-[10px] font-bold tracking-widest">CBU/CVU: </span>{order.pagoDetalle.cbu}</div>
                    )}
                    {order.pagoDetalle?.titular && (
                      <div className="mt-1 text-xs text-zinc-400"><span className="text-zinc-500 uppercase text-[10px] font-bold tracking-widest">Titular: </span>{order.pagoDetalle.titular}</div>
                    )}
                    {order.pagoDetalle?.instrucciones && <p className="text-zinc-500 mt-2 text-xs">{order.pagoDetalle.instrucciones}</p>}
                  </div>
                </div>
                {order.pagoDetalle?.alias && (
                  <div className="mt-3">
                    <button
                      data-testid="open-mp-app-button"
                      onClick={openMpApp}
                      className="w-full flex items-center justify-center gap-2 bg-[#009EE3] hover:bg-[#0088C7] text-white font-black uppercase text-[11px] tracking-widest py-2.5 rounded transition-colors"
                    >
                      <Smartphone className="w-4 h-4" /> Abrir Mercado Pago
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-3 p-4 bg-black/40 rounded-lg border border-line">
                {isCash ? <Banknote className="w-5 h-5 text-[#10B981] mt-0.5" /> : <CreditCard className="w-5 h-5 text-[#009EE3] mt-0.5" />}
                <div className="flex-1 text-sm">
                  <div className="font-bold text-white uppercase tracking-wide">
                    {isCash ? "Pagás en efectivo al recibir" : "Pago confirmado"}
                  </div>
                  <p className="text-zinc-500 mt-1 text-xs">
                    {isCash
                      ? "Te vamos a llamar para confirmar. Preparanos el efectivo para cuando llegue el delivery."
                      : "Recibimos tu pago y ya estamos preparando todo. Te avisamos por WhatsApp."}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-black/40 rounded-lg border border-line">
                <Clock className="w-4 h-4 text-gold mb-1" />
                <div className="text-xs uppercase text-zinc-500 tracking-widest">Tiempo estimado</div>
                <div className="font-heading text-lg text-white">35-45 min</div>
              </div>
              <div className="p-3 bg-black/40 rounded-lg border border-line">
                <MapPin className="w-4 h-4 text-brand mb-1" />
                <div className="text-xs uppercase text-zinc-500 tracking-widest">{order.entrega === "retiro" ? "Retiro" : "Envío a"}</div>
                <div className="text-sm text-white line-clamp-1">{order.entrega === "retiro" ? `En el local${dirLocal ? ` · ${dirLocal}` : ""}` : order.direccion}</div>
                {order.entrega !== "retiro" && order.zona && (
                  <div data-testid="success-zona" className="text-[10px] text-zinc-500 mt-0.5">{order.zona} · reparte {order.reparte} · {order.distanciaKm} km</div>
                )}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">Tu pedido</div>
              <div className="space-y-2">
                {order.items.map((i, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-white">
                      <span className="text-gold font-bold">{i.cantidad}×</span> {i.nombre}
                    </span>
                    <span className="text-zinc-400">{formatARS(i.precioUnitario * i.cantidad)}</span>
                  </div>
                ))}
              </div>
              {(order.promosAplicadas || []).map((p, i) => (
                <div
                  key={i}
                  data-testid={`success-promo-${p.promoId || i}`}
                  className="mt-3 flex items-center justify-between bg-brand/10 border border-brand/30 rounded-lg px-3 py-2.5"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Tag className="w-4 h-4 text-brand shrink-0" strokeWidth={3} />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-brand font-black">Promo aplicada</div>
                      <div className="text-sm font-bold text-white truncate">{p.nombre}</div>
                    </div>
                  </div>
                  <div className="font-heading text-lg text-brand shrink-0 ml-2">-{formatARS(p.ahorro)}</div>
                </div>
              ))}
              {order.entrega !== "retiro" && (order.envio > 0 || order.zona) && (
                <div data-testid="success-envio-line" className="mt-3 flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Envío{order.zona ? ` · ${order.zona}` : ""}</span>
                  <span className={order.envio > 0 ? "text-zinc-300 font-bold" : "text-[#25D366] font-bold"}>{order.envio > 0 ? formatARS(order.envio) : "Gratis"}</span>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-line flex items-center justify-between">
                <span className="uppercase text-sm tracking-widest text-zinc-400 font-bold">Total</span>
                <span className="font-heading text-3xl text-gold">{formatARS(order.total)}</span>
              </div>
              {order.ahorro > 0 && (
                <div data-testid="success-ahorro-total" className="mt-2 text-right text-xs font-bold uppercase tracking-widest text-[#25D366]">
                  Ahorraste {formatARS(order.ahorro)} con promos
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              {wsp && (
                <a
                  data-testid="order-whatsapp-cta"
                  href={`https://wa.me/${wsp}?text=${encodeURIComponent(`Hola! Consulto por mi pedido ${order.codigo}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#0EA574] text-black font-black uppercase text-sm tracking-widest py-3 rounded transition-colors"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={3} /> WhatsApp
                </a>
              )}
              <Link
                to="/"
                data-testid="back-home-button"
                className="flex-1 flex items-center justify-center gap-2 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm tracking-widest py-3 rounded transition-colors"
              >
                <Home className="w-4 h-4" /> Volver
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
